import type {
  InferenceWorkerPath,
  InferenceWorkerRequest,
  InferenceWorkerResponse,
} from '@/services/bayesianApi/inferenceWorkerProtocol'

// KBE-840 PR 2: the main thread's side of the inference worker.
//
// Imports the PROTOCOL and nothing else, so pulling this into `trpc.ts` -- which every
// page loads -- costs no engine code. The worker's own dependency graph reaches it
// through `new Worker(new URL(...))`, which bundlers turn into a separate chunk.

/**
 * The part of `Worker` this uses -- structural, so a test can drive the bridge with a
 * plain object. Handler PROPERTIES rather than `addEventListener` because there is
 * exactly one worker and one handler of each kind, which is also what spares both
 * sides the `EventListener` casts.
 */
export type WorkerLike = {
  postMessage: (message: InferenceWorkerRequest) => void
  terminate: () => void
  onmessage: ((event: MessageEvent<InferenceWorkerResponse>) => void) | null
  // `ErrorEvent` rather than the one field this reads: a handler parameter is
  // contravariant, so narrowing it here would make the real `Worker` unassignable.
  onerror: ((event: ErrorEvent) => void) | null
}

type PendingRequest = {
  resolve: (data: unknown) => void
  reject: (error: Error) => void
}

export type InferenceWorkerBridge = {
  run: (path: InferenceWorkerPath, input: unknown) => Promise<unknown>
}

/**
 * A worker plus the bookkeeping that keeps concurrent requests apart.
 *
 * Every request carries an id the reply echoes, which is the whole of the concurrency
 * design: two outstanding requests cannot receive each other's answers.
 *
 * There is deliberately no CANCELLATION, and the cost of that is known and accepted
 * rather than overlooked. Because this is ONE worker, a superseded request still runs to
 * completion and delays the live one behind it, so rapid assumption toggling pays for
 * every intermediate state before the newest answer arrives -- at roughly a second per
 * inference, three or four quick toggles is a visible wait. Accepted for ~5 researchers
 * (2026-08-18): a cancel protocol costs a message type and a race per request, and the
 * alternatives are worse than they look -- terminate-on-supersession pays a WASM kernel
 * recompile every time (the compile is memoized per PROCESS, and a fresh worker is a
 * fresh process) and needs a supersession signal the bridge does not have, while
 * `op.signal` would require `abortOnUnmount` and so change abort behavior for every
 * query in the app. If the lag becomes the complaint, terminate-on-supersession scoped
 * to `bayesian.infer` is the cheapest real fix.
 *
 * Note the earlier reasoning here was wrong in a way worth remembering: it argued a
 * superseded reply is free because React Query has already dropped that query. True of
 * the REPLY, but it ignored that the computation behind it still occupies the only
 * worker. (`terminate()` on a FAILED worker is a different thing, and is done -- see
 * `failAll`.)
 *
 * `spawn` is a parameter so tests can drive the bridge with a fake worker; nothing in
 * `src/` passes one but the module's own singleton.
 */
export function createInferenceWorkerBridge(
  spawn: () => WorkerLike,
): InferenceWorkerBridge {
  let worker: WorkerLike | undefined
  let nextRequestId = 0
  const pending = new Map<number, PendingRequest>()

  const failAll = (error: Error): void => {
    // The worker itself failed, so nothing in flight can still be answered, and the
    // handle is discarded rather than reused: the next request spawns a fresh worker,
    // which is what makes a failed script load recoverable instead of hanging every
    // inference query for the life of the tab.
    //
    // TERMINATED, not just dropped. An `error` event does not necessarily kill the
    // thread, and a dropped handle we never terminate leaks a live worker holding the
    // WASM kernel and a prepared 629-node network on its heap -- once per failure, for
    // the life of the tab. This is not the cancellation the ticket ruled out: it kills
    // a worker already declared dead, not one still serving a request.
    worker?.terminate()
    worker = undefined
    const failed = [...pending.values()]
    pending.clear()
    for (const request of failed) request.reject(error)
  }

  const handleMessage = (
    event: MessageEvent<InferenceWorkerResponse>,
  ): void => {
    const response = event.data
    const request = pending.get(response.id)
    if (!request) return
    pending.delete(response.id)

    if (response.ok) {
      request.resolve(response.data)
      return
    }
    request.reject(new Error(response.message))
  }

  const ensureWorker = (): WorkerLike => {
    if (worker) return worker

    const spawned = spawn()
    spawned.onmessage = handleMessage
    spawned.onerror = (event) => {
      // Only while this worker is still the current one. `failAll` is indiscriminate --
      // it empties the whole shared `pending` map -- and a discarded worker can fire
      // `error` again after its replacement is serving, which would reject the
      // REPLACEMENT's in-flight requests and undo the recovery this exists for.
      //
      // Its `onmessage` needs no such guard: request ids are monotonic per bridge, so a
      // late reply from a discarded worker can only name an id `failAll` already
      // cleared, which `handleMessage` ignores.
      if (worker !== spawned) return
      failAll(
        new Error(
          // `||`, not `??`: `ErrorEvent.message` is non-optional and is the EMPTY
          // STRING for a failed module-worker load, which is the most likely failure
          // here -- `??` would let that through and end the message at a bare colon.
          `The local inference worker failed to run: ${event.message || 'unknown error'}`,
        ),
      )
    }
    worker = spawned
    return spawned
  }

  return {
    run: (path, input) =>
      new Promise<unknown>((resolve, reject) => {
        const target = ensureWorker()
        const id = nextRequestId++
        pending.set(id, { resolve, reject })
        const request: InferenceWorkerRequest = { id, path, input }
        try {
          target.postMessage(request)
        } catch (error) {
          // A structured-clone failure rejects before anything is in flight. Without
          // this the entry sits in `pending` for the life of the tab and the caller's
          // promise never settles -- a hang rather than an error.
          pending.delete(id)
          reject(error instanceof Error ? error : new Error(String(error)))
        }
      }),
  }
}

let bridge: InferenceWorkerBridge | undefined

/**
 * The worker is created on FIRST REQUEST, not at import: a researcher on a
 * `LBP_ON_MODAL` network never spawns one, and the URL below is what makes the engine a
 * separate chunk rather than part of the page load.
 *
 * `new Worker(new URL(..., import.meta.url))` is the one form every bundler we build
 * with understands (turbopack dev, the turbopack production build). Do not replace it
 * with a computed path or a string -- the static form is what the bundler matches on.
 */
export function runInferenceInWorker(
  path: InferenceWorkerPath,
  input: unknown,
): Promise<unknown> {
  bridge ??= createInferenceWorkerBridge(
    () =>
      new Worker(new URL('../../workers/inferenceWorker.ts', import.meta.url), {
        type: 'module',
      }),
  )
  return bridge.run(path, input)
}
